import { useMemo } from "react";

interface ServerSeoValues {
  title?: string;
  description?: string;
  image?: string;
  author?: string;
  canonicalUrl?: string;
}

/**
 * Reads SEO values from server-rendered meta tags on mount.
 *
 * nginx/njs injects these tags for social crawlers. We read them at app root
 * and pass to a root-level PageHead so react-helmet-async preserves them
 * instead of resetting to defaults during hydration.
 */
export function useServerSeoValues(): ServerSeoValues {
  return useMemo(() => {
    const getMeta = (attr: string, value: string): string | undefined => {
      const el = document.querySelector(`meta[${attr}="${value}"]`);
      return el?.getAttribute("content") || undefined;
    };

    // Extract title without site suffix
    const fullTitle = document.title;
    const title = fullTitle.endsWith(" | noma.to")
      ? fullTitle.replace(/ \| noma\.to$/, "")
      : fullTitle === "noma.to - Off the beaten path"
        ? undefined
        : fullTitle || undefined;

    return {
      title,
      description: getMeta("name", "description"),
      image: getMeta("property", "og:image"),
      author: getMeta("name", "author"),
      canonicalUrl: document.querySelector("link[rel='canonical']")?.getAttribute("href") || undefined,
    };
  }, []);
}
